import { alpha, darken, lighten, type Theme } from "@mui/material/styles";

export type ChartThresholdSeverity =
  "normal" | "warning" | "medium" | "high" | "critical" | "moderate" | "long";

export interface EChartsThresholdStyle {
  /** Muted fill used by severity-coloured bars. */
  barColor: string;
  /** Clear stroke used by severity-coloured lines. */
  lineColor: string;
  /** Subtle dashed reference-line and label colour. */
  referenceLineColor: string;
  /** A restrained brighter variant of barColor for native ECharts emphasis. */
  barHoverColor: string;
}

export type EChartsThemeOverride = Omit<Partial<EChartsThemeTokens>, "threshold"> & {
  threshold?: Partial<{
    [Severity in ChartThresholdSeverity]: Partial<EChartsThresholdStyle>;
  }>;
};

export interface EChartsThemeTokens {
  backgroundColor: string;
  textColor: string;
  mutedTextColor: string;
  gridLineColor: string;
  axisLineColor: string;
  tooltipBackgroundColor: string;
  tooltipBorderColor: string;
  primarySeriesColor: string;
  rangeFillColor: string;
  /** Subtle single-series area fill, distinct from rangeFillColor's expected-range semantics. */
  areaFillColor: string;
  /** Semantic presentation only; threshold values remain chart configuration. */
  threshold: Record<ChartThresholdSeverity, EChartsThresholdStyle>;
  /** Transparent dark overlay used to identify periods without observations. */
  missingDataColor?: string;
  /** Ordered accent colors for multi-category series such as bar rankings and donut slices. */
  categoricalPalette: string[];
  /** Makes a series color distinct from a configured threshold color. */
  getThresholdSeriesColor: (thresholdColor: string) => string;
  highlightColor: string;
  highlightTextColor: string;
  fontFamily: string;
}

export function createEChartsTheme(
  theme: Theme,
  overrides?: EChartsThemeOverride
): EChartsThemeTokens {
  const defaults: EChartsThemeTokens = {
    backgroundColor: "transparent",
    textColor: theme.palette.text.primary,
    mutedTextColor: theme.palette.text.secondary,
    gridLineColor: alpha(theme.palette.common.white, 0.06),
    axisLineColor: alpha(theme.palette.common.white, 0.14),
    tooltipBackgroundColor: theme.palette.background.paper,
    tooltipBorderColor: alpha(theme.palette.common.white, 0.14),
    primarySeriesColor: theme.palette.common.white,
    rangeFillColor: alpha(theme.palette.secondary.main, 0.24),
    areaFillColor: alpha(theme.palette.secondary.main, 0.18),
    threshold: {
      normal: {
        barColor: alpha(theme.palette.success.main, 0.68),
        lineColor: theme.palette.success.light,
        referenceLineColor: alpha(theme.palette.success.light, 0.72),
        barHoverColor: alpha(lighten(theme.palette.success.main, 0.12), 0.78),
      },
      warning: {
        barColor: alpha(theme.palette.warning.light, 0.68),
        lineColor: theme.palette.warning.light,
        referenceLineColor: alpha(theme.palette.warning.light, 0.82),
        barHoverColor: alpha(lighten(theme.palette.warning.light, 0.08), 0.82),
      },
      medium: {
        barColor: alpha(theme.palette.warning.main, 0.72),
        lineColor: theme.palette.warning.main,
        referenceLineColor: alpha(theme.palette.warning.main, 0.82),
        barHoverColor: alpha(lighten(theme.palette.warning.main, 0.08), 0.82),
      },
      high: {
        barColor: alpha(theme.palette.error.light, 0.72),
        lineColor: theme.palette.error.light,
        referenceLineColor: alpha(theme.palette.error.light, 0.82),
        barHoverColor: alpha(lighten(theme.palette.error.light, 0.06), 0.82),
      },
      critical: {
        barColor: alpha(theme.palette.error.dark, 0.78),
        lineColor: theme.palette.error.dark,
        referenceLineColor: alpha(darken(theme.palette.error.dark, 0.08), 0.92),
        barHoverColor: alpha(lighten(theme.palette.error.dark, 0.08), 0.86),
      },
      moderate: {
        barColor: alpha(theme.palette.warning.main, 0.68),
        lineColor: theme.palette.warning.light,
        referenceLineColor: alpha(theme.palette.warning.light, 0.72),
        barHoverColor: alpha(lighten(theme.palette.warning.main, 0.12), 0.78),
      },
      long: {
        barColor: alpha(theme.palette.error.main, 0.68),
        lineColor: theme.palette.error.light,
        referenceLineColor: alpha(theme.palette.error.light, 0.72),
        barHoverColor: alpha(lighten(theme.palette.error.main, 0.1), 0.78),
      },
    },
    missingDataColor: alpha(theme.palette.common.black, 0.3),
    categoricalPalette: [
      theme.palette.secondary.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.error.main,
      theme.palette.success.main,
    ],
    getThresholdSeriesColor: (thresholdColor) => lighten(thresholdColor, 0.25),
    highlightColor: theme.palette.common.white,
    highlightTextColor: theme.palette.background.default,
    fontFamily: theme.typography.fontFamily ?? "sans-serif",
  };

  if (overrides == null) return defaults;

  return {
    ...defaults,
    ...overrides,
    threshold: {
      normal: { ...defaults.threshold.normal, ...overrides.threshold?.normal },
      warning: { ...defaults.threshold.warning, ...overrides.threshold?.warning },
      medium: { ...defaults.threshold.medium, ...overrides.threshold?.medium },
      high: { ...defaults.threshold.high, ...overrides.threshold?.high },
      critical: { ...defaults.threshold.critical, ...overrides.threshold?.critical },
      moderate: { ...defaults.threshold.moderate, ...overrides.threshold?.moderate },
      long: { ...defaults.threshold.long, ...overrides.threshold?.long },
    },
  };
}
